import api from '../httpClient';

export type AIModelKind = 'text' | 'realtime' | 'transcription' | 'image' | 'tts' | 'embedding' | 'moderation' | 'other';

export type AISettingsFieldKind =
  | 'model' | 'choice' | 'text' | 'messages' | 'int' | 'float' | 'string' | 'language_codes' | 'boolean' | 'email';

export type AISettingsField = {
  key: string;
  label: string;
  kind: AISettingsFieldKind;
  choices?: string[];
  choice_labels?: Record<string, string>;
  model_kind?: AIModelKind;
  min?: number;
  max?: number;
  allow_blank?: boolean;
  help?: string;
};

export type AIPromptMessage = { role: string; content: string };

export type AISettingsValue = string | number | boolean | AIPromptMessage[];

export type AISettingsValues = Record<string, AISettingsValue>;

/** Observation from a small save-time request, not a model capability guarantee. */
export type AIProviderCheck = {
  checked_at: string;
  api: 'responses' | 'chat';
  model: string;
  reasoning_effort: string | null;
  verbosity: string | null;
  requested_service_tier: string | null;
  service_tier: string | null;
  structured_output: boolean;
};

export type AIFeatureSetting = {
  key: string;
  label: string;
  group: string;
  description: string;
  provider: 'openai' | 'elevenlabs' | 'google' | 'app';
  /** 'ai' settings live on Admin > AI Settings; 'app' settings on Admin > Operations. */
  area?: 'ai' | 'app';
  fields: AISettingsField[];
  prompt_variables: string[];
  defaults: AISettingsValues;
  overrides: AISettingsValues;
  /** Saved overrides ignored because the shipped prompt they were written for changed. */
  stale_fields?: string[];
  /** The ignored saved values, so staff can review and save them again. */
  stale_overrides?: AISettingsValues;
  effective: AISettingsValues;
  revision: number;
  updated_at: string | null;
  updated_by: string | null;
  provider_check?: AIProviderCheck | null;
};

export type AIModelCatalogEntry = {
  id: string;
  created: number | null;
  owned_by: string;
  kind: AIModelKind;
};

export type AIModelCatalog = {
  models: AIModelCatalogEntry[];
  fetched_at: string | null;
  last_error: string;
  last_error_at: string | null;
  api_key_configured: boolean;
};

export type AISettingsOverview = {
  features: AIFeatureSetting[];
  catalog: AIModelCatalog;
};

export type AIUsageTotals = {
  requests: number;
  errors: number;
  error_rate: number | null;
  input_tokens: number;
  output_tokens: number;
  avg_duration_ms: number | null;
};

export type AIUsageRow = {
  name: string;
  label?: string;
  feature_key?: string | null;
  requests: number;
  errors: number;
  total_tokens: number;
  avg_duration_ms: number | null;
};

export type AIUsageSummary = {
  days: number;
  start: string;
  end: string;
  current: AIUsageTotals;
  previous: AIUsageTotals;
  by_feature: AIUsageRow[];
  by_model: AIUsageRow[];
};

export type AIFeatureRevision = {
  revision: number;
  action: 'update' | 'reset' | 'import';
  values: AISettingsValues;
  previous_values: AISettingsValues;
  note: string;
  created_at: string;
  created_by: string | null;
};

/** A null value clears that field's override. */
export type AIFeatureUpdate = Record<string, AISettingsValue | null>;

/** Edit, reset, and history calls shared by AI Settings and app settings. */
export type SettingsClient = {
  updateFeature: (key: string, values: AIFeatureUpdate, expectedRevision: number) => Promise<{ data: AIFeatureSetting }>;
  resetFeature: (key: string, expectedRevision: number) => Promise<{ data: AIFeatureSetting }>;
  getHistory: (key: string) => Promise<{ data: { revisions: AIFeatureRevision[] } }>;
  restoreRevision: (key: string, revision: number, expectedRevision: number) => Promise<{ data: AIFeatureSetting }>;
};

const createSettingsClient = (base: string): SettingsClient => {
  const featureUrl = (key: string) => `${base}/features/${encodeURIComponent(key)}/`;
  return {
    updateFeature: (key, values, expectedRevision) =>
      api.patch<AIFeatureSetting>(featureUrl(key), { values, expected_revision: expectedRevision }),
    resetFeature: (key, expectedRevision) =>
      api.delete<AIFeatureSetting>(featureUrl(key), { data: { expected_revision: expectedRevision } }),
    getHistory: (key) => api.get<{ revisions: AIFeatureRevision[] }>(`${featureUrl(key)}history/`),
    restoreRevision: (key, revision, expectedRevision) =>
      api.post<AIFeatureSetting>(`${featureUrl(key)}history/`, { revision, expected_revision: expectedRevision }),
  };
};

export const aiSettings = {
  ...createSettingsClient('/api/admin/ai-settings'),
  getOverview: () => api.get<AISettingsOverview>('/api/admin/ai-settings/'),
  getUsage: () => api.get<AIUsageSummary>('/api/admin/ai-settings/usage/'),
  getModels: () => api.get<AIModelCatalog>('/api/admin/ai-settings/models/'),
  refreshModels: () => api.post<AIModelCatalog>('/api/admin/ai-settings/models/'),
};

export const appSettings = {
  ...createSettingsClient('/api/admin/app-settings'),
  getOverview: () => api.get<{ features: AIFeatureSetting[] }>('/api/admin/app-settings/'),
};
